import { useState, useRef, useCallback } from "react";
import { getCenterData } from "../services/nanooxService";
import {
  getCenterIdentity,
  getFromCenterCache,
  saveToCenterCache,
} from "../cache/centerCache";

function buildOrderedResults(centros, centerMap) {
  return centros
    .map((center) => centerMap.get(getCenterIdentity(center)))
    .filter(Boolean);
}

function deduplicateCenters(centros) {
  const unique = new Map();
  centros.forEach((center) => {
    const key = getCenterIdentity(center);
    if (key && !unique.has(key)) unique.set(key, center);
  });
  return Array.from(unique.values());
}

export function useCentersDataAllSafe() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cancelRef = useRef(false);
  const requestIdRef = useRef(0);

  const reset = useCallback(() => {
    cancelRef.current = true;
    requestIdRef.current += 1;
    setData([]);
    setLoading(false);
    setError(null);
    cancelRef.current = false;
  }, []);

  const startLoading = useCallback(
    async ({ centros = [], servidor, delay = 400 }) => {
      if (!servidor || !centros.length) return;
      const uniqueCenters = deduplicateCenters(centros);

      const requestId = ++requestIdRef.current;
      cancelRef.current = false;
      setLoading(true);
      setError(null);
      let successfulCenters = 0;
      let failedCenters = 0;

      setData((previous) => {
        const previousMap = new Map(
          previous.map((item) => [getCenterIdentity(item), item])
        );
        return buildOrderedResults(uniqueCenters, previousMap);
      });

      console.log(`[MULTI LOAD START] ${uniqueCenters.length} centros`);

      for (const center of uniqueCenters) {
        if (cancelRef.current || requestId !== requestIdRef.current) break;

        const cached = getFromCenterCache(center);
        if (cached) {
          console.log(`[CACHE HIT - MULTI] ${center.centro}`);
          successfulCenters += 1;
          setData((previous) => {
            const nextMap = new Map(
              previous.map((item) => [getCenterIdentity(item), item])
            );

            nextMap.set(getCenterIdentity(center), {
              ...cached,
              region: cached?.region || center.region,
              area: cached?.area || center.area,
              centro: center.centro,
            });

            return buildOrderedResults(uniqueCenters, nextMap);
          });
          continue;
        }

        try {
          console.log(`[MULTI GET] ${center.centro}`);

          const centerData = await getCenterData({
            region: center.region,
            area: center.area,
            centro: center.centro,
            centerName: center.centerName,
            serverUrl: servidor,
          });

          if (cancelRef.current || requestId !== requestIdRef.current) break;

          saveToCenterCache(center, centerData);
          successfulCenters += 1;

          setData((previous) => {
            const nextMap = new Map(
              previous.map((item) => [getCenterIdentity(item), item])
            );

            nextMap.set(getCenterIdentity(center), {
              ...centerData,
              region: centerData?.region || center.region,
              area: centerData?.area || center.area,
              centro: center.centro,
            });

            return buildOrderedResults(uniqueCenters, nextMap);
          });
        } catch (err) {
          failedCenters += 1;
          console.warn(`[MULTI ERROR] ${center.centro}`);
        }

        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      if (requestId === requestIdRef.current && !cancelRef.current) {
        if (successfulCenters === 0 && failedCenters > 0) {
          setError("No fue posible consultar los centros Nanoox.");
        }
        setLoading(false);
        console.log(`[MULTI LOAD END] ${uniqueCenters.length} centros procesados`);
      }
    },
    []
  );

  return {
    data,
    loading,
    error,
    startLoading,
    reset,
  };
}
